import type {ReactElement} from 'react';
import React from 'react';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import {BaseVectorColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-vector-color-option';
import type {TranscriptBackgroundOptionProps} from './transcript-background-option.types';
import {useGetKeyFromSubtitlePropertiesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitlePropertiesForTranscriptItem';
import {useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem';
import type {RGBA} from '@Utils/color.util';
import {convertFillObjectToFabricColor, convertFabricColorToFillObject} from '@Utils/color.util';
import type {TSerializedFabricColor} from '@PosterWhiteboard/items/item/item.types';

export function TranscriptBackgroundOption({...props}: TranscriptBackgroundOptionProps): ReactElement<any> {
  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in TranscriptBackgroundOption');
  }

  const backgroundBorderRadius = useGetKeyFromSubtitlePropertiesForTranscriptItem('backgroundBorderRadius');
  if (backgroundBorderRadius === undefined) {
    throw new Error('Transcript item does not have backgroundBorderRadius in its subtitle objects hashmap');
  }

  const bgFillColor = useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem('fillColor');
  if (bgFillColor === undefined) {
    throw new Error('Transcript item subtitle objects do not have a fillColor property in the backgroundFill object');
  }

  const bgFillType = useGetKeyFromSubtitleBackgroundFillPropertiesForActiveTranscriptItem('fillType');
  if (bgFillType === undefined) {
    throw new Error('Transcript item subtitle objects do not have a fillType property in the backgroundFill object');
  }

  const onBackgroundOpacityChanged = async (updatedValue: number, undoable = true): Promise<void> => {
    const fillColor = activeItem.getAnySubtitle().backgroundFill?.fillColor?.slice() as unknown as RGBA[];
    for (let i = 0; i < fillColor.length; i++) {
      const color = fillColor[i].slice();
      color[3] = updatedValue;
      fillColor[i] = color as RGBA;
    }

    void activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        backgroundFill: {
          fillColor,
        },
      },
      {undoable}
    );
  };

  const onChangeBackgroundFillType = async (newFill: TSerializedFabricColor): Promise<void> => {
    const newFillObject = convertFabricColorToFillObject(newFill);
    if (newFillObject.fillType === bgFillType) {
      return;
    }

    await activeItem.updateAllSubtitlesStylesFromSubtitleObject({
      backgroundFill: newFillObject,
    });
  };

  const onBackgroundRoundnessChanged = async (updatedValue: number, undoable = true): Promise<void> => {
    await activeItem.updateAllSubtitlesStylesFromSubtitleObject(
      {
        backgroundBorderRadius: updatedValue,
      },
      {undoable}
    );
  };

  return (
    <BaseVectorColorOption
      label={window.i18next.t('pmwjs_background')}
      fillObject={convertFillObjectToFabricColor({
        fillType: bgFillType,
        fillColor: bgFillColor,
      })}
      onChangeFill={onChangeBackgroundFillType}
      onClickColorFill={() => {
        props.onTranscriptBackgroundColorFillClicked();
      }}
      onChangeOpacity={onBackgroundOpacityChanged}
      showRoundnessOption
      fillRoundness={backgroundBorderRadius}
      onChangeRoundness={onBackgroundRoundnessChanged}
    />
  );
}
